import { Link } from "react-router-dom";
import { Container } from "reactstrap";
import styled from "styled-components";
import NewFooter from "../../components/Footers/NewFooter";
import NewNavbar from "../../components/Navbar/NewNavbar";
import { PageContainer } from "../../components/styled/shared";
import { landingPage } from "../../constants/descriptions";
import {
  COMPARISON,
  EDIT_KINDS,
  FAQS,
  HERO,
  HONESTY,
  PHOTO_STEPS,
  PRICING_POINTS,
  VIDEO_STEPS,
} from "../../content/photoVideo";

const MainContent = styled.div`
  flex: 1;
  padding: 120px 0 80px;

  @media (max-width: 767.98px) {
    padding: 100px 0 60px;
  }
`;

const PageTitle = styled.h1`
  font-size: 46px;
  font-weight: 700;
  color: var(--brand-ink);
  text-align: center;
  margin: 0 auto 20px;
  max-width: 860px;
  line-height: 1.2;

  @media (max-width: 767.98px) {
    font-size: 34px;
  }
`;

const PageSubtitle = styled.p`
  font-size: 19px;
  color: var(--brand-ink-3);
  text-align: center;
  max-width: 760px;
  margin: 0 auto 28px;
  line-height: 1.65;
`;

const HeroActions = styled.div`
  text-align: center;
  margin-bottom: 56px;
`;

const HeroNote = styled.p`
  font-size: 15px;
  color: var(--brand-ink-4);
  margin: 14px 0 0;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: var(--brand-primary);
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  padding: 14px 30px;
  border-radius: var(--brand-radius);
  text-decoration: none;
  box-shadow: var(--brand-shadow-primary);
  transition: background 0.15s ease, transform 0.15s ease;

  &:hover {
    background: var(--brand-primary-600);
    transform: translateY(-1px);
    color: #fff;
    text-decoration: none;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 4px var(--brand-primary-100);
  }
`;

const Section = styled.section`
  background: #fff;
  border: 1px solid var(--brand-border);
  border-radius: var(--brand-radius-xl);
  box-shadow: var(--brand-shadow);
  padding: 48px;
  max-width: 940px;
  margin: 0 auto 28px;

  @media (max-width: 767.98px) {
    padding: 32px 22px;
    border-radius: var(--brand-radius-lg);
  }
`;

const SectionTitle = styled.h2`
  font-size: 30px;
  font-weight: 700;
  color: var(--brand-ink);
  margin: 0 0 16px;

  @media (max-width: 767.98px) {
    font-size: 25px;
  }
`;

const Paragraph = styled.p`
  font-size: 16px;
  color: var(--brand-ink-2);
  line-height: 1.75;
  margin: 0 0 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InlineLink = styled(Link)`
  color: var(--brand-primary-700);
  font-weight: 600;
`;

/* --- Before / after samples (real Listino touch-ups, already public) --- */
const SampleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 8px;

  @media (max-width: 767.98px) {
    grid-template-columns: 1fr;
  }
`;

const Sample = styled.figure`
  margin: 0;
`;

const SampleCaption = styled.figcaption`
  font-size: 14px;
  font-weight: 600;
  color: var(--brand-ink-3);
  margin-bottom: 10px;
`;

const SamplePair = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const SampleFrame = styled.div`
  position: relative;
  border: 1px solid var(--brand-border);
  border-radius: var(--brand-radius);
  overflow: hidden;
  background: var(--brand-bg-soft);
`;

const SampleImg = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

const SampleLabel = styled.span`
  position: absolute;
  left: 8px;
  bottom: 8px;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 700;
  color: ${(/** @type {{ $variant?: string }} */ p) =>
    p.$variant === "after" ? "var(--brand-primary-700)" : "var(--brand-ink-3)"};
`;

/* --- Cards / steps --- */
const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px;
  margin-top: 8px;
`;

const Card = styled.div`
  background: var(--brand-bg-soft);
  border: 1px solid var(--brand-border);
  border-radius: var(--brand-radius);
  padding: 20px;
`;

const CardTitle = styled.h3`
  font-size: 17px;
  font-weight: 700;
  color: var(--brand-ink);
  margin: 0 0 8px;
`;

const CardBody = styled.p`
  font-size: 15px;
  color: var(--brand-ink-3);
  line-height: 1.6;
  margin: 0;
`;

const StepList = styled.ol`
  list-style: none;
  counter-reset: step;
  margin: 8px 0 0;
  padding: 0;
`;

const Step = styled.li`
  counter-increment: step;
  position: relative;
  padding: 0 0 22px 52px;
  border-left: 2px solid var(--brand-border);
  margin-left: 15px;

  &:last-child {
    padding-bottom: 0;
    border-left-color: transparent;
  }

  &::before {
    content: counter(step);
    position: absolute;
    left: -16px;
    top: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--brand-primary);
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

/* --- Video review-gate illustration (CSS only; no photo, nothing faked) --- */
const MockCaption = styled.p`
  font-size: 13px;
  color: var(--brand-ink-4);
  margin: 10px 0 0;
  text-align: center;
`;

const Mock = styled.div`
  margin-top: 28px;
  border: 1px solid var(--brand-border);
  border-radius: var(--brand-radius-lg);
  background: #fff;
  overflow: hidden;
  max-width: 460px;
  margin-left: auto;
  margin-right: auto;
`;

const MockFrame = styled.div`
  aspect-ratio: 16 / 9;
  background: linear-gradient(160deg, var(--brand-ink-2), var(--brand-ink));
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.82);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-align: center;
  padding: 0 20px;
`;

const MockBody = styled.div`
  padding: 16px;
`;

const MockCheck = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 13px;
  color: var(--brand-ink-3);
  line-height: 1.5;

  &::before {
    content: "";
    flex: none;
    width: 16px;
    height: 16px;
    margin-top: 2px;
    border: 2px solid var(--brand-border-strong);
    border-radius: 4px;
  }
`;

const MockButton = styled.div`
  margin-top: 14px;
  background: var(--brand-border-strong);
  color: var(--brand-ink-3);
  border-radius: var(--brand-radius-sm);
  padding: 10px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
`;

/* --- Lists --- */
const Points = styled.ul`
  margin: 8px 0 0;
  padding-left: 22px;
`;

const Point = styled.li`
  font-size: 16px;
  color: var(--brand-ink-2);
  line-height: 1.7;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Reasons = styled.div`
  display: grid;
  gap: 20px;
  margin-top: 8px;
`;

const Reason = styled.div`
  border-left: 3px solid var(--brand-primary-100);
  padding-left: 18px;
`;

const ReasonTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: var(--brand-ink);
  margin: 0 0 6px;
`;

const Callout = styled.section`
  background: var(--brand-primary-50);
  border: 1px solid var(--brand-primary-100);
  border-radius: var(--brand-radius-xl);
  padding: 40px 48px;
  max-width: 940px;
  margin: 0 auto 28px;

  @media (max-width: 767.98px) {
    padding: 28px 22px;
    border-radius: var(--brand-radius-lg);
  }
`;

const FaqItem = styled.div`
  border-top: 1px solid var(--brand-border);
  padding: 20px 0;

  &:last-child {
    padding-bottom: 0;
  }
`;

const FaqQuestion = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: var(--brand-ink);
  margin: 0 0 8px;
`;

const CTASection = styled.div`
  background: var(--brand-bg-softer, #fff);
  border: 1px solid var(--brand-border);
  border-radius: var(--brand-radius-xl);
  padding: 44px;
  max-width: 940px;
  margin: 0 auto;
  text-align: center;

  @media (max-width: 767.98px) {
    padding: 32px 22px;
  }
`;

const CTATitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: var(--brand-ink);
  margin: 0 0 12px;
`;

const samples = landingPage.touchups.photoTouchups.samples;

const PhotoVideoPage = () => {
  return (
    <PageContainer>
      <NewNavbar />

      <MainContent>
        <Container>
          <PageTitle>{HERO.title}</PageTitle>
          <PageSubtitle>{HERO.subtitle}</PageSubtitle>
          <HeroActions>
            <CTAButton to={HERO.ctaPath}>{HERO.ctaLabel}</CTAButton>
            <HeroNote>{HERO.freeNote}</HeroNote>
          </HeroActions>

          <Section aria-labelledby="samples-heading">
            <SectionTitle id="samples-heading">Real listing photos, edited</SectionTitle>
            <Paragraph>
              These are Listino touch-ups on real listing photos. Each edit works on the space that
              was already in front of the camera — the light, the lawn, the clutter — and the photo
              it came from stays right beside it.
            </Paragraph>
            <SampleGrid>
              {samples.map((sample) => (
                <Sample key={sample.caption}>
                  <SampleCaption>{sample.caption}</SampleCaption>
                  <SamplePair>
                    <SampleFrame>
                      <SampleImg
                        src={sample.originalUrl}
                        alt={`${sample.caption} — before`}
                        width="800"
                        height="600"
                        loading="lazy"
                      />
                      <SampleLabel>Before</SampleLabel>
                    </SampleFrame>
                    <SampleFrame>
                      <SampleImg
                        src={sample.editedUrl}
                        alt={`${sample.caption} — after`}
                        width="800"
                        height="600"
                        loading="lazy"
                      />
                      <SampleLabel $variant="after">After</SampleLabel>
                    </SampleFrame>
                  </SamplePair>
                </Sample>
              ))}
            </SampleGrid>
          </Section>

          <Section aria-labelledby="review-heading">
            <SectionTitle id="review-heading">What the photo review names</SectionTitle>
            <Paragraph>
              Preparing a listing includes a pass over every photo on it. The review reads each shot
              and says which ones could look better and what would help — in these terms:
            </Paragraph>
            <CardGrid>
              {EDIT_KINDS.map((kind) => (
                <Card key={kind.title}>
                  <CardTitle>{kind.title}</CardTitle>
                  <CardBody>{kind.body}</CardBody>
                </Card>
              ))}
            </CardGrid>
          </Section>

          <Section aria-labelledby="photo-steps-heading">
            <SectionTitle id="photo-steps-heading">How a photo touch-up happens</SectionTitle>
            <StepList>
              {PHOTO_STEPS.map((step) => (
                <Step key={step.title}>
                  <CardTitle>{step.title}</CardTitle>
                  <CardBody>{step.body}</CardBody>
                </Step>
              ))}
            </StepList>
          </Section>

          <Section aria-labelledby="video-heading">
            <SectionTitle id="video-heading">
              A property video built from the listing&apos;s photos
            </SectionTitle>
            <Paragraph>
              Once a listing is prepared, its photos can be assembled into a short property video.
              There is nothing to film and nothing to upload again — and nothing gets out before you
              have watched it.
            </Paragraph>
            <StepList>
              {VIDEO_STEPS.map((step) => (
                <Step key={step.title}>
                  <CardTitle>{step.title}</CardTitle>
                  <CardBody>{step.body}</CardBody>
                </Step>
              ))}
            </StepList>

            <Mock aria-hidden="true">
              <MockFrame>Preview — built from this listing&apos;s photos</MockFrame>
              <MockBody>
                <MockCheck>
                  I watched the entire preview and verified it does not add, remove, or misrepresent
                  any property feature
                </MockCheck>
                <MockButton>Approve</MockButton>
              </MockBody>
            </Mock>
            <MockCaption>
              An illustration of the review step — the Approve button stays off until you confirm
              you watched the clip.
            </MockCaption>
          </Section>

          <Section aria-labelledby="compare-heading">
            <SectionTitle id="compare-heading">
              How this differs from a standalone photo or video app
            </SectionTitle>
            <Paragraph>
              Plenty of apps will edit a real estate photo or turn a folder of pictures into a reel.
              The difference here is not the edit — it is that the editing is part of preparing the
              listing, so the work of deciding what to change is already done.
            </Paragraph>
            <Reasons>
              {COMPARISON.map((row) => (
                <Reason key={row.title}>
                  <ReasonTitle>{row.title}</ReasonTitle>
                  <CardBody>{row.body}</CardBody>
                </Reason>
              ))}
            </Reasons>
          </Section>

          <Section aria-labelledby="pricing-heading">
            <SectionTitle id="pricing-heading">What it costs</SectionTitle>
            <Paragraph>
              Listino is priced in credits, and photos and video come out of the same balance as
              everything else it prepares.
            </Paragraph>
            <Points>
              {PRICING_POINTS.map((point) => (
                <Point key={point}>{point}</Point>
              ))}
            </Points>
            <Paragraph style={{ marginTop: 20 }}>
              The full menu, the plans, and the credit packs are on the{" "}
              <InlineLink to="/pricing">pricing page</InlineLink>.
            </Paragraph>
          </Section>

          <Callout aria-labelledby="honesty-heading">
            <SectionTitle id="honesty-heading">{HONESTY.title}</SectionTitle>
            <Paragraph>{HONESTY.body}</Paragraph>
            <Paragraph>
              <InlineLink to={HONESTY.linkPath}>{HONESTY.linkLabel}</InlineLink>
            </Paragraph>
          </Callout>

          <Section aria-labelledby="faq-heading">
            <SectionTitle id="faq-heading">Questions agents ask</SectionTitle>
            {FAQS.map((faq) => (
              <FaqItem key={faq.question}>
                <FaqQuestion>{faq.question}</FaqQuestion>
                <CardBody>{faq.answer}</CardBody>
              </FaqItem>
            ))}
          </Section>

          <CTASection>
            <CTATitle>Start with the address</CTATitle>
            <Paragraph>
              Prepare a listing and the photo review comes with it. Touch-ups and video are there
              afterwards, for the photos and the clips you choose.
            </Paragraph>
            <CTAButton to={HERO.ctaPath}>{HERO.ctaLabel}</CTAButton>
            <HeroNote>{HERO.freeNote}</HeroNote>
          </CTASection>
        </Container>
      </MainContent>

      <NewFooter />
    </PageContainer>
  );
};

export default PhotoVideoPage;
